import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Bed, Bath, Car, Square } from "lucide-react";

const TYPES = [
  { id: "apartamento", label: "Apartamento" },
  { id: "casa", label: "Casa" },
  { id: "cobertura", label: "Cobertura" },
  { id: "terreno", label: "Terreno" },
  { id: "comercial", label: "Comercial" },
];

const EMPTY = {
  title: "", type: "apartamento", address: "", neighborhood: "", city: "",
  price: 0, bedrooms: 0, bathrooms: 0, parking: 0, area: 0,
  description: "", photo_url: "", listing_type: "venda",
};

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/properties");
      setProperties(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditingId(p.id); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Título obrigatório"); return; }
    try {
      if (editingId) await api.patch(`/properties/${editingId}`, form);
      else await api.post("/properties", form);
      toast.success("Imóvel salvo");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir esse imóvel?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Imóvel excluído");
    load();
  };

  return (
    <div className="p-5 lg:p-10 max-w-7xl mx-auto" data-testid="properties-root">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Carteira</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C19]">Imóveis</h1>
          <p className="text-stone-500 mt-1 text-sm">{properties.length} cadastrados</p>
        </div>
        <Button data-testid="add-property-btn" onClick={openCreate} className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white">
          <Plus className="w-4 h-4 mr-2" /> Novo imóvel
        </Button>
      </div>

      {properties.length === 0 && !loading ? (
        <Card className="p-12 text-center border-dashed border-stone-300">
          <div className="text-stone-500 mb-4">Ainda não tem nenhum imóvel cadastrado.</div>
          <Button onClick={openCreate} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
            <Plus className="w-4 h-4 mr-2" /> Cadastrar primeiro imóvel
          </Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {properties.map((p) => (
            <Card key={p.id} data-testid={`property-card-${p.id}`} className="overflow-hidden border-stone-200 hover:shadow-md transition group">
              <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                {p.photo_url ? (
                  <img src={p.photo_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300">
                    <Square className="w-12 h-12" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Badge className="bg-[#1B3A2F]/10 text-[#1B3A2F] hover:bg-[#1B3A2F]/10 capitalize">{p.type}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{p.listing_type}</Badge>
                </div>
                <div className="font-heading font-bold text-base text-[#1C1C19] line-clamp-1">{p.title}</div>
                {(p.neighborhood || p.city) && (
                  <div className="text-xs text-stone-500 mt-0.5">{[p.neighborhood, p.city].filter(Boolean).join(", ")}</div>
                )}
                <div className="font-heading text-xl font-bold text-[#D4651A] mt-2">{formatBRL(p.price)}</div>
                <div className="flex gap-3 mt-3 text-xs text-stone-500">
                  {p.bedrooms > 0 && <span className="inline-flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{p.bedrooms}</span>}
                  {p.bathrooms > 0 && <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.bathrooms}</span>}
                  {p.parking > 0 && <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5" />{p.parking}</span>}
                  {p.area > 0 && <span>{p.area}m²</span>}
                </div>
                <div className="flex gap-2 mt-4 border-t border-stone-100 pt-3">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-red-600">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar imóvel" : "Novo imóvel"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Título *">
              <Input data-testid="property-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Apto 2 dorm Barreiros" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo">
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Finalidade">
                <Select value={form.listing_type} onValueChange={(v) => setForm({...form, listing_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="venda">Venda</SelectItem>
                    <SelectItem value="aluguel">Aluguel</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Endereço">
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bairro">
                <Input value={form.neighborhood} onChange={(e) => setForm({...form, neighborhood: e.target.value})} />
              </Field>
              <Field label="Cidade">
                <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} />
              </Field>
            </div>
            <Field label="Preço (R$)">
              <Input type="number" data-testid="property-price-input" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})} />
            </Field>
            <div className="grid grid-cols-4 gap-2">
              <Field label="Dorm."><Input type="number" value={form.bedrooms} onChange={(e) => setForm({...form, bedrooms: parseInt(e.target.value) || 0})} /></Field>
              <Field label="Banh."><Input type="number" value={form.bathrooms} onChange={(e) => setForm({...form, bathrooms: parseInt(e.target.value) || 0})} /></Field>
              <Field label="Vagas"><Input type="number" value={form.parking} onChange={(e) => setForm({...form, parking: parseInt(e.target.value) || 0})} /></Field>
              <Field label="Área m²"><Input type="number" value={form.area} onChange={(e) => setForm({...form, area: parseFloat(e.target.value) || 0})} /></Field>
            </div>
            <Field label="URL da foto">
              <Input value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} placeholder="https://..." />
            </Field>
            <Field label="Descrição">
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-property-btn" onClick={save} className="bg-[#1B3A2F] hover:bg-[#244A3C]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-stone-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
